import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  forwardRef,
  Input,
  OnInit,
  inject,
  input,
  InputSignal,
  signal,
  WritableSignal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { ScaleQuestionAnswer } from '../../../../../models/answer/scale-answer.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { addIcons } from 'ionicons';
import { star, starOutline, starHalf } from 'ionicons/icons';
import { IonButton, IonIcon } from '@ionic/angular/standalone';

type OnChangeCallback = (newValue: ScaleQuestionAnswer) => void;
type OnTouchCallback = () => void;

@Component({
  selector: 'lib-star-control',
  templateUrl: './star-control.component.html',
  styleUrls: ['./star-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StarControlComponent),
      multi: true,
    },
  ],
  imports: [IonButton, IonIcon],
})
export class StarControlComponent implements ControlValueAccessor, OnInit {
  private readonly changeDetectorRef: ChangeDetectorRef =
    inject(ChangeDetectorRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /**
   * Boolean which determines if the user can change the rating.
   * (if formControl is disabled.
   */
  public readonly readonly: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * Icon used to show the active stars. (Or other icons, whatever you wish.)
   */
  public readonly activeIcon: InputSignal<string> = input<string>('star');

  /**
   * Icon used to show the non active stars. (Or other icons, whatever you wish.)
   */
  public readonly defaultIcon: InputSignal<string> =
    input<string>('star-outline');

  /**
   * Icon used to show a half active / half non active star. (Or other icons
   * whatever you wish.)
   */
  public readonly halfIcon: InputSignal<string> = input<string>('star-half');

  /**
   * Boolean which determines if the rating can have half stars.
   */
  public readonly halfStar: InputSignal<boolean> = input<boolean>(false);

  /**
   * The maximum rating that can be given. Also equals to the amount of stars (icons).
   */
  public readonly maxRating: InputSignal<number> = input<number>(5);

  /**
   * The current rating state.
   */
  private readonly _rating: BehaviorSubject<number | null> =
    new BehaviorSubject<number | null>(0);

  /**
   * The current highlight state.
   */
  private readonly _highlightIndex: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);

  public get highlightIndex(): number {
    return this._highlightIndex.getValue();
  }

  /**
   * @inheritDoc
   */
  //eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: OnChangeCallback = (_newValue: number) => {};

  /**
   * @inheritDoc
   */
  //eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouch: OnTouchCallback = () => {};

  constructor() {
    addIcons({ star, starOutline, starHalf });
  }

  /**
   * @inheritDoc
   */
  public ngOnInit(): void {
    this._rating
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rating) => this.onChange(rating as number));
  }

  /**
   * @inheritDoc
   */
  public registerOnChange(fn: OnChangeCallback): void {
    this.onChange = fn;
  }

  /**
   * @inheritDoc
   */
  public registerOnTouched(fn: OnTouchCallback): void {
    this.onTouch = fn;
  }

  /**
   * @inheritDoc
   */
  public writeValue(rating: number | null): void {
    if (this.readonly()) {
      return;
    }
    this._rating.next(rating);
    // The active stars are not directly shown when a value is set, because
    // _determineIconColor() is not re-running the change detection.
    this.changeDetectorRef.detectChanges();
  }

  /**
   * @inheritDoc
   */
  public setDisabledState(isDisabled: boolean): void {
    this.readonly.set(isDisabled);
  }

  /**
   * Onclick method which changes the current rating, based on the icon the users
   * clicked on.
   * @param event
   */
  public _changeRating(event: MouseEvent): void {
    if (this.readonly()) {
      return;
    }

    const target: HTMLElement = event.target as HTMLElement;
    const id: number = parseInt(target.id, 10);

    // If the mouse click was on the first half of the icon.
    const firstHalveOfIcon: boolean =
      event.x < target.offsetLeft + target.offsetWidth / 2;

    this._rating.next(this.halfStar() && firstHalveOfIcon ? id + 0.5 : id + 1);
  }

  /**
   * Method which determines if the index (star) should have the active class.
   * @param index
   */
  public _determineIconColor(index: number): boolean {
    return index < Math.ceil(this._rating.getValue() as number);
  }

  /**
   * Method determines if the given index (star) should have the highlight class.
   * @param index
   */
  public _determineHighlightIconColor(index: number): boolean {
    const highlight: number = this._highlightIndex.getValue();
    const rating: number = this._rating.getValue()
      ? (this._rating.getValue() as number)
      : 0;
    const currentStar: number = index + 1;

    // Current star is already selected so no highlight.
    if (currentStar <= Math.floor(rating)) {
      return false;
    }

    // If we are on the last active star and it contains half a star
    // only highlight if the mouse is on the right side of the star.
    if (currentStar === Math.ceil(rating) && currentStar !== rating) {
      return highlight > rating;
    }

    // Index is higher than rating but lower than highlight.
    return (
      currentStar > Math.ceil(rating) && currentStar <= Math.ceil(highlight)
    );
  }

  /**
   * This method determines if the icon of the given index should display a
   * activeIcon, defaultIcon or halfIcon.
   * @param index
   */
  public _determineIcon(index: number): string {
    const highlight: number = this._highlightIndex.getValue();
    const currentStar: number = index + 1;
    // only show half star if this is set on true
    if (this.halfStar() === true && this.ratingIsOnHalveAIndex(index)) {
      return currentStar <= highlight ? this.activeIcon() : this.halfIcon();
    }

    if (index < Math.floor(this._rating.getValue() as number)) {
      return this.activeIcon();
    }

    if (currentStar <= Math.ceil(highlight)) {
      if (currentStar === Math.ceil(highlight) && currentStar !== highlight) {
        return this.halfIcon();
      }
      return this.activeIcon();
    }

    return this.defaultIcon();
  }

  /**
   * Returns true if the given index is halve index.
   **/
  private ratingIsOnHalveAIndex(index: number): boolean {
    // When the current index if higher than the current rating
    // But we still have a left over
    // Then the rating is on half an index.
    const rating: number = this._rating.getValue() as number;
    return rating - index > 0 && rating - index <= 0.5;
  }

  /**
   * On mouse move inside the star (icon) this function determines on what half of
   * the star the mouse is so we can set the highlighting and icons accordingly.
   * @param event
   */
  public _setHighlightIndex(event: MouseEvent): void {
    if (this.readonly()) {
      return;
    }
    const target: HTMLElement = event.target as HTMLElement;
    const id: number = parseInt(target.id, 10);

    // If the mouse click was on the first half of the icon.
    const firstHalveOfIcon: boolean =
      event.x < target.offsetLeft + target.offsetWidth / 2;

    const newHighlightIndex: number =
      this.halfStar() && firstHalveOfIcon ? id + 0.5 : id + 1;

    this._highlightIndex.next(newHighlightIndex);
  }

  /**
   * If the mouse leave the rating box the highlightIndex is set to 0.
   */
  public _resetHighlightIndex(): void {
    this._highlightIndex.next(0);
  }
}
